import React, { useContext, useEffect, useState } from 'react';
import { NavigateFunction, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
	Button,
	ContentLoader,
	ContentWrapper,
	Hourglass,
	PageLead,
	Wrapper,
	withLocalization,
} from '@adac-mp/components';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';
import {
	ILabel,
	OfferContext,
	ITypeVignettenFehlerseitenFields,
	countryUrlMap,
	getSessionExpiredCollection,
	setRouteLangParam,
	getLabelValue,
	getUrlBasePath,
} from '@adac-mp/shared';
import { useQuery } from '@apollo/client';
import { useFlowId } from '@adac-mp/components/src/hooks/useFlowId';

const renderButtonGroup = (
	navigate: NavigateFunction,
	supplierId: number | null,
	errorLabels: ITypeVignettenFehlerseitenFields | undefined,
	labels: ILabel[],
	lang: string | undefined,
) => {
	return (
		<div className="flex flex-col justify-center sticky left-0 right-0 bottom-0 p-1.5 gap-1.5 bg-white -mb-3 border-t border-t-gray-700 md:m-0 md:border-t-0 md:bg-transparent md:flex-row md:static md:gap-4 md:p-0">
			<Button className="flex-1" variant="secondary" href="https://www.adac.de/fahrzeugwelt/maut-vignette">
				{getLabelValue('mp.backToStart', labels) || 'Zurück zur Startseite'}
			</Button>
			<Button
				className="flex-1"
				onClick={() => {
					if (supplierId !== null) {
						navigate(getUrlBasePath(`/${countryUrlMap[supplierId]}/configure${setRouteLangParam(lang)}`));
					} else {
						window.location.href = 'https://www.adac.de/fahrzeugwelt/maut-vignette';
					}
				}}>
				{errorLabels?.sessionExpiredContinueLabel || 'Weiter einkaufen'}
			</Button>
		</div>
	);
};

const SessionExpired = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { viewport } = useViewport();
	const { lang } = useParams();
	const flowId = useFlowId();
	const { labels } = useContext(OfferContext);
	const [errorType, setErrorType] = useState<string | null>(null);
	const { error, data, loading } = useQuery(getSessionExpiredCollection, {
		variables: { lang: lang || 'de' },
	});

	const errorLabels = !error ? data?.vignettenFehlerseitenCollection.items[0] : undefined;

	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);
		const urlVariant = urlParams.get('type');
		setErrorType(urlVariant);
	}, []);

	const renderHeadlineText = () => {
		if (errorType === 'payment') return errorLabels?.expiredPaymentTitle || '';
		return errorLabels?.sessionExpiredTitle || 'Ihre Sitzung ist abgelaufen.';
	};

	const renderDescriptionText = () => {
		if (errorType === 'payment') return errorLabels?.expiredPaymentText || '';
		return (
			errorLabels?.sessionExpiredText || (
				<>
					Ihre Sitzung wurde beendet, da Sie zu lange inaktiv waren.
					<br />
					Bitte starten Sie den Prozess erneut.
				</>
			)
		);
	};

	return (
		<>
			{loading ? (
				<ContentLoader isLoading hasPortal={false} />
			) : (
				<>
					<Wrapper className="bg-white min-h-[calc(100vh_-_56px)] sm:min-h-[calc(100vh_-_98px)] items-center">
						<ContentWrapper>
							<PageLead
								icon={<Hourglass />}
								headline={renderHeadlineText()}
								description={renderDescriptionText()}
							/>
							{viewport === 'desktop' && renderButtonGroup(navigate, flowId, errorLabels, labels, lang)}
						</ContentWrapper>
					</Wrapper>
					{viewport === 'mobile' && renderButtonGroup(navigate, flowId, errorLabels, labels, lang)}
				</>
			)}
		</>
	);
};

export const SessionExpiredPage = withLocalization(SessionExpired);
